import { inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpService } from '../http/http.service';
import {
  SupportAdminOption,
  SupportAlertPreferences,
  SupportAttachment,
  SupportInboundTicketCategory,
  SupportOutboundTicketPayload,
  SupportSettings,
  SupportStatsWindow,
  SupportTicketDetail,
  SupportTicketDirection,
  SupportTicketListResponse,
  SupportTicketRegion,
  SupportTikTokConnection,
  SupportTikTokDisconnectResult,
  SupportTicketStats,
  SupportTicketStatus,
  SupportTicketSummary,
  SupportUserSummary,
  SupportUserTicketStats,
  SupportAiSuggestion,
} from '../../common/general-types/support-ticket';

@Injectable({
  providedIn: 'root',
})
export class SupportService {
  private readonly httpService = inject(HttpService);

  readonly assignedOpenTicketCount = signal(0);
  /** Total open tickets in the admin queue (support-admin top-bar badge). */
  readonly openTicketCount = signal(0);
  /** Open tickets where support last replied (user has a reply waiting). */
  readonly unreadUserTicketCount = signal(0);

  refreshAssignedOpenTicketCount(): void {
    this.httpService
      .runHttpCall('GET', '/support/admin/tickets/assigned-count', 'application/json')
      .subscribe({
        next: (response: { count: number }) => {
          this.assignedOpenTicketCount.set(response?.count ?? 0);
        },
        error: () => {
          this.listAdminTickets({ status: 'open', assigned: 'mine', take: 1 }).subscribe({
            next: (response) => this.assignedOpenTicketCount.set(response.total),
            error: () => this.assignedOpenTicketCount.set(0),
          });
        },
      });
  }

  refreshOpenTicketCount(): void {
    this.listAdminTickets({ status: 'open', assigned: 'all', take: 1 }).subscribe({
      next: (response) => this.openTicketCount.set(response.total),
      error: () => this.openTicketCount.set(0),
    });
    this.refreshAssignedOpenTicketCount();
  }

  refreshUnreadUserTicketCount(): void {
    this.listTickets('open').subscribe({
      next: (tickets) => {
        const unread = tickets.filter((ticket) => ticket.lastMessageAuthorType === 'admin').length;
        this.unreadUserTicketCount.set(unread);
      },
      error: () => this.unreadUserTicketCount.set(0),
    });
  }

  createTicket(payload: {
    category: SupportInboundTicketCategory;
    subject: string;
    message: string;
    businessAccountId?: string;
    attachments?: SupportAttachment[];
    alertPreferences: SupportAlertPreferences;
    appVersion?: string;
    appOs?: string;
  }): Observable<SupportTicketDetail> {
    return this.httpService
      .runHttpCall('POST', '/support/tickets', 'application/json', payload)
      .pipe(tap(() => this.refreshUnreadUserTicketCount()));
  }

  listTickets(status?: SupportTicketStatus | 'all'): Observable<SupportTicketSummary[]> {
    const query = status && status !== 'all' ? `?status=${status}` : '';
    return this.httpService.runHttpCall('GET', `/support/tickets${query}`, 'application/json');
  }

  /** Public response-time snapshot for the support home card (Mon–Fri business hours). */
  getTicketStats(window: SupportStatsWindow = '30d'): Observable<SupportUserTicketStats> {
    return this.httpService.runHttpCall(
      'GET',
      `/support/tickets/stats?window=${window}`,
      'application/json',
    );
  }

  getTicket(ticketId: string): Observable<SupportTicketDetail> {
    return this.httpService.runHttpCall('GET', `/support/tickets/${ticketId}`, 'application/json');
  }

  addUserMessage(
    ticketId: string,
    body: string,
    attachments?: SupportAttachment[],
  ): Observable<SupportTicketDetail> {
    return this.httpService
      .runHttpCall('POST', `/support/tickets/${ticketId}/messages`, 'application/json', {
        body,
        attachments,
      })
      .pipe(tap(() => this.refreshUnreadUserTicketCount()));
  }

  closeUserTicket(ticketId: string): Observable<SupportTicketDetail> {
    return this.httpService
      .runHttpCall('PATCH', `/support/tickets/${ticketId}/close`, 'application/json', {})
      .pipe(tap(() => this.refreshUnreadUserTicketCount()));
  }

  updateUserAlertPreferences(
    ticketId: string,
    alertPreferences: SupportAlertPreferences,
  ): Observable<SupportTicketDetail> {
    return this.httpService.runHttpCall(
      'PATCH',
      `/support/tickets/${ticketId}/alert-preferences`,
      'application/json',
      alertPreferences,
    );
  }

  getTicketByToken(token: string): Observable<SupportTicketDetail> {
    return this.httpService.runHttpCall(
      'GET',
      `/support/tickets/access/${token}`,
      'application/json',
      undefined,
      false,
    );
  }

  addMessageByToken(
    token: string,
    body: string,
    attachments?: SupportAttachment[],
  ): Observable<SupportTicketDetail> {
    return this.httpService.runHttpCall(
      'POST',
      `/support/tickets/access/${token}/messages`,
      'application/json',
      { body, attachments },
      false,
    );
  }

  closeTicketByToken(token: string): Observable<SupportTicketDetail> {
    return this.httpService.runHttpCall(
      'PATCH',
      `/support/tickets/access/${token}/close`,
      'application/json',
      {},
      false,
    );
  }

  updateAlertPreferencesByToken(
    token: string,
    alertPreferences: SupportAlertPreferences,
  ): Observable<SupportTicketDetail> {
    return this.httpService.runHttpCall(
      'PATCH',
      `/support/tickets/access/${token}/alert-preferences`,
      'application/json',
      alertPreferences,
      false,
    );
  }

  /** Whether the signed-in user has a mobile device registered to receive push alerts. */
  getPushAvailability(): Observable<{ hasPushDevice: boolean }> {
    return this.httpService.runHttpCall(
      'GET',
      '/support/tickets/push-availability',
      'application/json',
    );
  }

  listAdminTickets(params?: {
    status?: SupportTicketStatus | 'all';
    assigned?: 'all' | 'mine' | 'unassigned';
    assignedAdminUserId?: string;
    direction?: SupportTicketDirection;
    region?: SupportTicketRegion;
    search?: string;
    skip?: number;
    take?: number;
  }): Observable<SupportTicketListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.assigned) searchParams.set('assigned', params.assigned);
    if (params?.direction) searchParams.set('direction', params.direction);
    if (params?.region) searchParams.set('region', params.region);
    if (params?.assignedAdminUserId) {
      searchParams.set('assignedAdminUserId', params.assignedAdminUserId);
    }
    if (params?.search?.trim()) searchParams.set('search', params.search.trim());
    if (params?.skip != null) searchParams.set('skip', String(params.skip));
    if (params?.take != null) searchParams.set('take', String(params.take));
    const query = searchParams.toString();
    return this.httpService.runHttpCall(
      'GET',
      `/support/admin/tickets${query ? `?${query}` : ''}`,
      'application/json',
    );
  }

  getAdminTicketStats(window: SupportStatsWindow = '30d'): Observable<SupportTicketStats> {
    return this.httpService.runHttpCall(
      'GET',
      `/support/admin/tickets/stats?window=${window}`,
      'application/json',
    );
  }

  getAdminTicket(ticketId: string): Observable<SupportTicketDetail> {
    return this.httpService.runHttpCall('GET', `/support/admin/tickets/${ticketId}`, 'application/json');
  }

  getTikTokConnection(ticketId: string): Observable<SupportTikTokConnection> {
    return this.httpService.runHttpCall(
      'GET',
      `/support/admin/tickets/${ticketId}/tiktok-connection`,
      'application/json',
    );
  }

  disconnectTikTok(ticketId: string): Observable<SupportTikTokDisconnectResult> {
    return this.httpService.runHttpCall(
      'POST',
      `/support/admin/tickets/${ticketId}/disconnect-tiktok`,
      'application/json',
    );
  }

  addAdminMessage(
    ticketId: string,
    body: string,
    attachments?: SupportAttachment[],
  ): Observable<SupportTicketDetail> {
    return this.httpService
      .runHttpCall('POST', `/support/admin/tickets/${ticketId}/messages`, 'application/json', {
        body,
        attachments,
      })
      .pipe(tap(() => this.refreshOpenTicketCount()));
  }

  updateAdminMessage(
    ticketId: string,
    messageId: string,
    body: string,
    attachments?: SupportAttachment[],
  ): Observable<SupportTicketDetail> {
    return this.httpService.runHttpCall(
      'PATCH',
      `/support/admin/tickets/${ticketId}/messages/${messageId}`,
      'application/json',
      { body, attachments },
    );
  }

  deleteAdminMessage(ticketId: string, messageId: string): Observable<SupportTicketDetail> {
    return this.httpService.runHttpCall(
      'DELETE',
      `/support/admin/tickets/${ticketId}/messages/${messageId}`,
      'application/json',
    );
  }

  assignTicket(ticketId: string, assignedAdminUserId: string | null): Observable<SupportTicketDetail> {
    return this.httpService
      .runHttpCall('PATCH', `/support/admin/tickets/${ticketId}/assign`, 'application/json', {
        assignedAdminUserId,
      })
      .pipe(tap(() => this.refreshOpenTicketCount()));
  }

  closeAdminTicket(ticketId: string): Observable<SupportTicketDetail> {
    return this.httpService
      .runHttpCall('PATCH', `/support/admin/tickets/${ticketId}/close`, 'application/json', {})
      .pipe(tap(() => this.refreshOpenTicketCount()));
  }

  searchSupportUsers(term: string): Observable<SupportUserSummary[]> {
    return this.httpService.runHttpCall(
      'GET',
      `/support/admin/users/search?q=${encodeURIComponent(term)}&take=20`,
      'application/json',
    );
  }

  createOutboundTicket(payload: SupportOutboundTicketPayload): Observable<SupportTicketDetail> {
    return this.httpService
      .runHttpCall('POST', '/support/admin/tickets', 'application/json', payload)
      .pipe(tap(() => this.refreshOpenTicketCount()));
  }

  listAssignableAdmins(): Observable<SupportAdminOption[]> {
    return this.httpService.runHttpCall('GET', '/support/admin/admins', 'application/json');
  }

  getSupportSettings(): Observable<SupportSettings> {
    return this.httpService.runHttpCall('GET', '/support/admin/settings', 'application/json');
  }

  updateSupportSettings(settings: Partial<SupportSettings>): Observable<SupportSettings> {
    return this.httpService.runHttpCall('PATCH', '/support/admin/settings', 'application/json', settings);
  }

  getAdminAiDraft(ticketId: string): Observable<SupportAiSuggestion | null> {
    return this.httpService.runHttpCall(
      'GET',
      `/support/admin/tickets/${ticketId}/ai-draft`,
      'application/json',
    );
  }

  createAdminAiDraft(ticketId: string, refresh = false): Observable<SupportAiSuggestion> {
    return this.httpService.runHttpCall(
      'POST',
      `/support/admin/tickets/${ticketId}/ai-draft`,
      'application/json',
      { refresh },
    );
  }

  approveAdminAiAction(ticketId: string, actionId: string): Observable<SupportAiSuggestion> {
    return this.httpService.runHttpCall(
      'POST',
      `/support/admin/tickets/${ticketId}/ai-actions/${actionId}/approve`,
      'application/json',
      {},
    );
  }

  recordAdminAiFeedback(
    ticketId: string,
    sentBody: string,
    suggestionId?: string,
  ): Observable<{ recorded: boolean }> {
    return this.httpService.runHttpCall(
      'POST',
      `/support/admin/tickets/${ticketId}/ai-feedback`,
      'application/json',
      { sentBody, suggestionId },
    );
  }
}
